import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { LocationPinIcon } from '@/shared/ui/icons/LocationPinIcon'
import { CarouselArrows } from '@/shared/ui/CarouselArrows'
import { useRowCarousel } from '@/shared/composables/useRowCarousel'
import { makeLabeler, useCommonCodes } from '@/shared/lib/codes'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { profileTone } from '@/shared/lib/profileTone'
import { api } from '@/shared/lib/api'
import { PUBLIC_STALE_TIME_MS } from '@/shared/lib/query'

export function RegisteredCompaniesRow({
  paddingTop = "pt-[60px]",
}: {
  paddingTop?: string;
} = {}) {
  const orgs = useQuery({
    queryKey: ["orgs", "home-pc"],
    queryFn: () => api.orgs(),
    staleTime: PUBLIC_STALE_TIME_MS,
  });
  const orgTypes = useCommonCodes("org_type");
  const orgTypeLabel = makeLabeler(orgTypes.data);
  const row = useRowCarousel();
  const { dongLabel } = useCardLabels();

  const items = orgs.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <section className={`mx-auto w-full max-w-full ${paddingTop}`} aria-label="등록된 기업">
      <div className="mb-[18px] flex items-center gap-3.5">
        <h2 className="m-0 min-w-px flex-1 text-body-lg font-semibold text-black-600">
          등록된 기업
        </h2>
      </div>

      <div className="relative">
        <ul
          ref={row.ref}
          className="m-0 flex list-none gap-7 overflow-x-auto p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((o) => (
            <li key={o.id} className="w-[143.33px] shrink-0">
              <Link
                className="flex flex-col gap-3 text-inherit no-underline"
                to={`/orgs/${o.id}`}
              >
                <span
                  aria-hidden="true"
                  className="flex aspect-square w-full items-center justify-center overflow-clip rounded-[20px] border-2 border-[rgb(0_0_0/5%)] text-black-100 [&>svg]:opacity-50"
                  style={{ background: profileTone(o.id) }}
                >
                  <CityIcon size={48} />
                </span>
                <span className="flex flex-col gap-2.5">
                  <span className="flex flex-col gap-0.5">
                    <span className="truncate text-body-md-reading font-medium text-black-500">
                      {o.name}
                    </span>
                    <span className="truncate text-body-sm text-black-100">
                      {orgTypeLabel(o.orgType)}
                    </span>
                  </span>
                  {o.baseRegionCode !== null && (
                    <span className="flex items-center gap-1 text-body-sm text-black-100">
                      <LocationPinIcon size={16} />
                      <span className="min-w-0 truncate">{dongLabel(o.baseRegionCode)}</span>
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <CarouselArrows control={row} label="등록된 기업" top={100} />
      </div>
    </section>
  );
}
